import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from 'framer-motion';
import {Menu, X} from 'lucide-react';
import Logo from '@/components/Logo';
import EditableText from '@/components/editable/EditableText';

const LINKS = [
{ id: 'vision', label: 'Vision' },
{ id: 'piliers', label: 'Piliers' },
{ id: 'poles', label: 'Pôles' },
{ id: 'publics', label: 'Publics' },
{ id: 'publications', label: 'Publications' },
{ id: 'partenaires', label: 'Partenaires' },
{ id: 'contact', label: 'Contact' }];


const Nav: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<string>('hero');
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, 'change', (v) => {
    setScrolled(v > 30);
  });

  // Scroll spy
  useEffect(() => {
    const ids = ['hero', ...LINKS.map((l) => l.id)];
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.
        filter((e) => e.isIntersecting).
        sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: '-40% 0px -50% 0px', threshold: [0.1, 0.3, 0.6] }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const onClick = (id: string) => {
    setOpen(false);
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-0 left-0 right-0 z-50">

        <motion.div
          className="border-b"
          animate={{
            backgroundColor: scrolled ? 'rgba(11,15,12,0.85)' : 'rgba(11,15,12,0)',
            borderColor: scrolled ? 'rgba(232,220,196,0.10)' : 'rgba(232,220,196,0)',
            backdropFilter: scrolled ? 'blur(20px)' : 'blur(0px)'
          }}
          transition={{ duration: 0.4 }}>

          <div className="max-w-[1440px] mx-auto px-6 lg:px-12 h-16 lg:h-20 flex items-center justify-between">
            <button
              onClick={() => onClick('hero')}
              className="flex items-center gap-3 group"
              data-cursor="hover">

              <Logo size={36} />
              <div className="hidden sm:flex flex-col leading-none">
                
                


              </div>
            </button>

            <nav className="hidden lg:flex items-center gap-1 relative">
              {LINKS.map((l) =>
              <button
                key={l.id}
                onClick={() => onClick(l.id)}
                className="relative px-4 py-2 text-[12px] tracking-mid uppercase text-[#C9BFA8] hover:text-[#E8DCC4] transition-colors"
                data-cursor="hover">

                  {active === l.id &&
                <motion.span
                  layoutId="nav-active"
                  className="absolute inset-0 rounded-full bg-[#C9E26A]/10 border border-[#C9E26A]/30"
                  transition={{ type: 'spring', stiffness: 280, damping: 28 }} />

                }
                  <span className="relative">
                    <EditableText contentKey={`nav.link.${l.id}`} section="Nav" label={`Lien — ${l.label}`} as="span">
                      {l.label}
                    </EditableText>
                  </span>
                </button>
              )}
            </nav>

            <div className="hidden lg:block">
              <motion.button
                onClick={() => onClick('contact')}
                className="relative inline-flex items-center gap-2 px-5 py-2.5 text-[11px] tracking-mid uppercase text-[#0E1B2C] bg-[#C9E26A] overflow-hidden"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                data-cursor="hover">

                <span className="relative z-10">
                  <EditableText contentKey="nav.cta" section="Nav" label="Bouton — Nous rejoindre" as="span">
                    Nous rejoindre
                  </EditableText>
                </span>
                <motion.span
                  className="absolute inset-0 bg-[#E8DCC4]"
                  initial={{ x: '-100%' }}
                  whileHover={{ x: '0%' }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} />

              </motion.button>
            </div>

            <button
              onClick={() => setOpen(true)}
              className="lg:hidden text-[#E8DCC4] p-2"
              aria-label="Ouvrir le menu">

              <Menu className="w-6 h-6" />
            </button>
          </div>
        </motion.div>
      </motion.header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open &&
        <>
            <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-[#0E1B2C]/80 backdrop-blur-sm z-[55] lg:hidden"
            onClick={() => setOpen(false)} />

            <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 bottom-0 w-[88%] max-w-sm bg-[#14253B] z-[56] lg:hidden flex flex-col">

              <div className="flex items-center justify-between px-6 h-16 border-b border-[#E8DCC4]/10">
                <div className="flex items-center gap-3">
                  <Logo size={32} />
                  <span className="font-serif-display text-[15px] text-[#E8DCC4]"><EditableText contentKey="nav.mobile.brand" section="Nav" label="Marque — menu mobile" as="span">Centre</EditableText></span>
                </div>
                <button
                onClick={() => setOpen(false)}
                className="text-[#E8DCC4] p-2"
                aria-label="Fermer">

                  <X className="w-5 h-5" />
                </button>
              </div>
              <motion.nav
              className="flex-1 flex flex-col gap-1 px-6 py-10"
              initial="hidden"
              animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.06, delayChildren: 0.1 } } }}>

                {LINKS.map((l) =>
              <motion.button
                key={l.id}
                onClick={() => onClick(l.id)}
                variants={{
                  hidden: { opacity: 0, x: 20 },
                  visible: { opacity: 1, x: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } }
                }}
                className="text-left font-serif-display text-[32px] text-[#E8DCC4] py-3 border-b border-[#E8DCC4]/8 hover:text-[#C9E26A] transition-colors">

                    <EditableText contentKey={`nav.link.${l.id}`} section="Nav" label={`Lien — ${l.label}`} as="span">
                      {l.label}
                    </EditableText>
                  </motion.button>
              )}
              </motion.nav>
              <div className="px-6 pb-8">
                <button
                onClick={() => onClick('contact')}
                className="w-full text-center px-6 py-4 text-[12px] tracking-mid uppercase bg-[#C9E26A] text-[#0E1B2C]">

                  <EditableText contentKey="nav.cta" section="Nav" label="Bouton — Nous rejoindre" as="span">
                    Nous rejoindre
                  </EditableText>
                </button>
              </div>
            </motion.aside>
          </>
        }
      </AnimatePresence>
    </>);

};

export default Nav;